import cx from 'classnames';
import * as _ from 'lodash';
import { Modal } from 'react-bootstrap';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { useGetComponentMatches, useGetComponents } from '../../../../../hooks/api/binary';
import { nuc } from '../../../../../utils';
import { icons, Link } from '../../../../generic';
import Accordion from '../../../../generic/Accordion';
import { BUTTON_TYPE, IconButton, LoaderButton } from '../../../../generic/Button';
import List from '../../../../generic/List';
import Loader from '../../../../generic/Loader';
import type { ModalProps } from '..';
import type { AccordionBodyContentProps } from '../../../../generic/Accordion/AccordionBody';

import styles from './BinaryMatchDetailsModal.module.less';

type MatchesListProps = AccordionBodyContentProps & {
  revisionId: string;
  componentId: string;
};

const MatchesList: VoidFunctionComponent<MatchesListProps> = ({ revisionId, componentId, isOpen }) => {
  const { data, isInitialLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useGetComponentMatches(
    revisionId,
    componentId,
    5,
    isOpen,
  );

  if (isInitialLoading) {
    return (
      <List>
        <List.Header>
          <div className={styles.matchListRow}>
            <div className={cx('fossa-font-size-sm', 'fossa-font-weight-medium', styles.matchListRowContent)}>
              String
            </div>
            <div className={cx('fossa-font-size-sm', 'fossa-font-weight-medium', styles.matchListRowContentCount)}>
              Count
            </div>
          </div>
        </List.Header>
        <List.Body>
          <List.RowLoading />
        </List.Body>
      </List>
    );
  }

  let rows: ReactNode[] = [];
  if (!data || _.isEmpty(data.pages) || _.isEmpty(data.pages[0]?.results)) {
    rows = [
      <List.RowContainer>
        <List.Row hasTightPadding isInteractive={false}>
          No matches found.
        </List.Row>
      </List.RowContainer>,
    ];
  } else {
    rows = data.pages.flatMap((page) => page.results.map((match) => (
      <List.RowContainer>
        <List.Row hasTightPadding isInteractive={false}>
          <div className={styles.matchListRow}>
            <div className={styles.matchListRowContent}>{match.matchString}</div>
            <div className={styles.matchListRowContentCount}>{match.count}</div>
          </div>
        </List.Row>
      </List.RowContainer>
    )));
  }

  return (
    <List>
      <List.Header>
        <div className={styles.matchListRow}>
          <div className={cx('fossa-font-size-sm', 'fossa-font-weight-medium', styles.matchListRowContent)}>String</div>
          <div className={cx('fossa-font-size-sm', 'fossa-font-weight-medium', styles.matchListRowContentCount)}>
            Count
          </div>
        </div>
      </List.Header>
      <List.Body>
        {rows}
        {hasNextPage && (
          <List.RowContainer>
            <div className={styles.matchListFooterRow}>
              <LoaderButton onClick={() => fetchNextPage()} isLoading={isFetchingNextPage} type={BUTTON_TYPE.SECONDARY}>
                Load More
              </LoaderButton>
            </div>
          </List.RowContainer>
        )}
      </List.Body>
    </List>
  );
};

type BinaryMatchModalProps = ModalProps;

const BinaryMatchDetailsModal: VoidFunctionComponent<BinaryMatchModalProps> = ({ modals, projectRevision }) => {
  const { isOpen, onClose } = modals.viewBinaryMatchDetailsModal;
  const { revision } = modals.viewBinaryMatchDetailsModal.context;

  // We know revision exists because of the Container.
  const { data, isFetchingNextPage, fetchNextPage, hasNextPage, isInitialLoading } = useGetComponents(
    projectRevision,
    revision as string,
    5,
  );

  let content: ReactNode = null;

  if (isInitialLoading) {
    content = <Loader />;
  } else if (!data || _.isEmpty(data.pages) || _.isEmpty(data.pages[0]?.results)) {
    content = <div>No matches found.</div>;
  } else {
    content = (
      <div>
        {data?.pages.map((page) =>
          page.results.map((component) => (
            <div className={styles.accordionContainer} key={component.componentId}>
              <Accordion id={component.componentId} isInitiallyOpen={false}>
                <Accordion.Target>
                  <div className={styles.accordionTargetContainer}>
                    <div className={styles.accordionTitle}>{component.filePath}</div>
                    <Link
                      to={`/api/binary/${nuc(projectRevision)}/${component.componentId}/matches/export`}
                      // Prevents clicking from opening/closing the accordion
                      shouldStopPropagation
                    >
                      <IconButton
                        icon={icons.downloadRegular}
                        id="match-details-download-button"
                        // Noop here since we let the Link do the work.
                        onClick={_.noop}
                      />
                    </Link>
                  </div>
                </Accordion.Target>
                <Accordion.Body>
                  <MatchesList revisionId={projectRevision} componentId={component.componentId} />
                </Accordion.Body>
              </Accordion>
            </div>
          )),
        )}
      </div>
    );
  }

  return (
    <Modal onHide={onClose} show={isOpen}>
      <Modal.Header>
        <Modal.Title>Match Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <span className={styles.contentContainer}>
          {content}
        </span>
        {hasNextPage && (
          <div className={styles.componentsLoadMoreButton}>
            <LoaderButton onClick={() => fetchNextPage()} isLoading={isFetchingNextPage} type={BUTTON_TYPE.SECONDARY}>
              {isFetchingNextPage ? 'Loading...' : 'Load More'}
            </LoaderButton>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

const BinaryMatchDetailsModalContainer: VoidFunctionComponent<BinaryMatchModalProps> = ({
  modals,
  projectRevision,
}) => {
  const { isOpen } = modals.viewBinaryMatchDetailsModal;
  const { revision } = modals.viewBinaryMatchDetailsModal.context;

  if (!isOpen || !revision || !projectRevision) {
    return null;
  }

  return <BinaryMatchDetailsModal modals={modals} projectRevision={projectRevision} />;
};

export default BinaryMatchDetailsModalContainer;
